import { Container as ContainerType } from '../../../types/canvas.types';

/**
 * Recursively finds a container by ID in the container tree
 */
export const findContainer = (
  containers: ContainerType[],
  targetId: string
): ContainerType | null => {
  for (const container of containers) {
    if (container.id === targetId) return container;
    if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
      const found = findContainer(container.content.nestedContainers, targetId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Finds the path to a container (array of container IDs from root to target)
 */
export const findContainerPath = (
  containers: ContainerType[],
  targetId: string,
  currentPath: string[] = []
): string[] | null => {
  for (const container of containers) {
    const containerPath = [...currentPath, container.id];

    if (container.id === targetId) {
      return containerPath;
    }

    if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
      const nestedPath = findContainerPath(container.content.nestedContainers, targetId, containerPath);
      if (nestedPath) return nestedPath;
    }
  }
  return null;
};

/**
 * Finds a container along with its parent information
 */
export const findContainerWithParent = (
  containers: ContainerType[],
  targetId: string,
  parentId: string | null = null
): {
  container: ContainerType | null;
  parentId: string | null;
  parentContainer: ContainerType | null;
} => {
  for (const container of containers) {
    if (container.id === targetId) {
      return { container, parentId, parentContainer: null };
    }

    if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
      const result = findContainerWithParent(container.content.nestedContainers, targetId, container.id);
      if (result.container) {
        return {
          ...result,
          parentContainer: result.parentId === container.id ? container : result.parentContainer
        };
      }
    }
  }
  return { container: null, parentId: null, parentContainer: null };
};

/**
 * Recursively updates containers using a transformation function
 */
export const updateContainersRecursively = (
  containers: ContainerType[],
  transform: (container: ContainerType) => ContainerType
): ContainerType[] => {
  return containers.map(container => {
    const transformedContainer = transform(container);

    if (transformedContainer.content.nestedContainers && transformedContainer.content.nestedContainers.length > 0) {
      return {
        ...transformedContainer,
        content: {
          ...transformedContainer.content,
          nestedContainers: updateContainersRecursively(transformedContainer.content.nestedContainers, transform)
        }
      };
    }

    return transformedContainer;
  });
};
